import { Component, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../core/auth/auth.service';
import { ButtonModule } from 'primeng/button';
import { filter } from 'rxjs/operators';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { LanguageSwitcherComponent } from '../../shared/components/language-switcher/language-switcher.component';
import { LibraryClassSelectorComponent } from '../../shared/components/library-class-selector/library-class-selector.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [
        RouterModule, 
        CommonModule, 
        ButtonModule, 
        TooltipModule, 
        MenuModule, 
        BreadcrumbModule,
        LanguageSwitcherComponent,
        LibraryClassSelectorComponent
    ],
   template: `
    <div class="h-12 border-b border-gray-200 bg-primary-500 flex justify-center items-center px-4 shadow-sm">
        <div class="flex justify-between items-center h-full w-full">
            
            <div class="flex items-center gap-3">
                <a class="flex flex-row items-center flex-shrink-0" routerLink="/home">
                    <img src="../../assets/images/logo-tesi.svg" alt="Logo" class="h-10 mr-2" />
                </a>
                
                <ng-container *ngIf="showLibrarySelector">
                    <i class="pi pi-chevron-right text-white font-bold -ml-3" style="font-size: 0.75rem"></i>
                    <app-library-class-selector 
                        [showLabel]="false"
                        [variant]="'topbar'"
                        [customStyleClass]="'breadcrumb-select min-w-[220px]'"
                        (userSelectionChange)="onLibrarySelectionChange()">
                        [size]="'small'">
                    </app-library-class-selector>
                </ng-container>

                <ng-container *ngIf="breadcrumbItems.length > 0">
                    <i class="pi pi-chevron-right text-white font-bold" style="font-size: 0.75rem" 
                       [class.-ml-3]="!showLibrarySelector"></i>
                    <p-breadcrumb 
                        [model]="breadcrumbItems" 
                        styleClass="border-none p-0 bg-transparent text-white text-sm font-bold">
                    </p-breadcrumb>
                </ng-container>
            </div>

            <div class="flex items-center gap-4">
                <app-language-switcher></app-language-switcher>
                
                <div class="layout-topbar-actions">
                    <div class="user-profile relative flex flex-row justify-between items-center gap-2" 
                         *ngIf="authService.isAuthenticated()">
                        <span class="text-white text-bold">{{ authService.userEmail() }}</span>
                        <p-button 
                            icon="pi pi-user" 
                            tooltipPosition="bottom" 
                            styleClass="h-8 w-8" 
                            size="small" 
                            (click)="menu.toggle($event)">
                        </p-button>
                    </div>
                    <p-menu #menu [model]="userMenuItems" [popup]="true"></p-menu>
                </div>
            </div>
        </div>
    </div>
`,
    styles: [`
        ::ng-deep .breadcrumb-select {
            background-color: rgba(255, 255, 255, 0.1);
            border-color: rgba(255, 255, 255, 0.3);
        }
        
        ::ng-deep .breadcrumb-select .p-select-label {
            color: white !important;
            font-weight: 600;
            font-size: 0.875rem;
        }
        
        ::ng-deep .breadcrumb-select .p-select-dropdown {
            color: white;
        }
        
        ::ng-deep .breadcrumb-select:not(.p-disabled):hover {
            border-color: rgba(255, 255, 255, 0.5);
            background-color: rgba(255, 255, 255, 0.15);
        }
        
        ::ng-deep .breadcrumb-select:not(.p-disabled).p-focus {
            border-color: white;
            box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.2);
            background-color: rgba(255, 255, 255, 0.15);
        }
        
        ::ng-deep .breadcrumb-select .p-placeholder {
            color: rgba(255, 255, 255, 0.7);
        }
    `]
})
export class AppTopbar implements OnInit {
    userMenuItems: MenuItem[] = [];
    breadcrumbItems: MenuItem[] = [];
    showLibrarySelector: boolean = false;

    // Inject services
    public authService = inject(AuthService);
    private router = inject(Router);
    private translate = inject(TranslateService);

    ngOnInit() {
        this.initializeMenuItems();
        this.updateBreadcrumb();

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.updateBreadcrumb();
        });

        this.translate.onLangChange.subscribe(() => {
            this.initializeMenuItems();
            this.updateBreadcrumb();
        });
    }

    signOut() {
        this.authService.logout();
    }

    private initializeMenuItems() {
        this.userMenuItems = [
            { 
                label: this.translate.instant('header.menu.sign-out'), 
                icon: 'pi pi-sign-out', 
                command: () => this.signOut() 
            }
        ];
    }

    onLibrarySelectionChange() {
        if (!this.router.url.startsWith('/home')) {
            this.router.navigate(['/home']);
        }
    }

    private updateBreadcrumb() {
        const url = this.router.url;
        const urlSegments = url.split('/').filter(segment => segment);

        let newBreadcrumbItems: MenuItem[] = [];
        this.showLibrarySelector = false;

        if (urlSegments.length === 0 && url === '/') {
            // Root path - no breadcrumb
        } else if (urlSegments[0] === 'home') {
            newBreadcrumbItems = [];
            this.showLibrarySelector = true;
        } else if (urlSegments[0] === 'doc-viewer') {
            newBreadcrumbItems = [
                { 
                    label: this.translate.instant('breadcrumb.document-viewer'), 
                    routerLink: `/doc-viewer` 
                }
            ];
            this.showLibrarySelector = true;
        }

        this.breadcrumbItems = newBreadcrumbItems;
    }
}